import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { set, takeRight } from 'lodash-es';
import { useCallback, useEffect } from 'react';
import SuperJSON from 'superjson';
import { systemSettingQueryOptions } from '@/hooks/files/use-system-setting';
import type { RespondedType, ResponseError } from '@/models/response';
import { getUserId } from '@/stores/auth';
import { addTranslateToast } from '@/stores/toast';
import { STORAGE_KEY_PREFIX } from '@/utils/constants';
import { fetcher } from '@/utils/fetcher';

const TELEMETRY_TRUNCATE_SIZE = 3_000;

async function superjsonParseAsync(value: string | null | undefined): Promise<unknown> {
  return new Promise((resolve) => {
    try {
      if (!value) {
        resolve(null);
      } else {
        resolve(SuperJSON.parse(value));
      }
    } catch {
      resolve(value);
    }
  });
}

async function collectTelemetry(): Promise<string> {
  const data: Record<string, unknown> = {};

  // 1. get all local storage
  const localStorageKeys = Object.keys(localStorage);
  for (const key of localStorageKeys) {
    if (!key.startsWith(STORAGE_KEY_PREFIX)) continue;
    try {
      const value = await superjsonParseAsync(localStorage.getItem(key));
      set(data, `ls.${key}`, value);
    } catch {
      set(data, `ls.${key}`, 'invalid data');
    }
  }

  // 2. get all session storage
  const sessionStorageKeys = Object.keys(sessionStorage);
  for (const key of sessionStorageKeys) {
    if (!key.startsWith(STORAGE_KEY_PREFIX)) continue;
    try {
      const value = await superjsonParseAsync(sessionStorage.getItem(key));
      set(data, `ss.${key}`, value);
    } catch {
      set(data, `ss.${key}`, 'invalid data');
    }
  }

  // 3. dump all values from every indexeddb database
  const databases = await window.indexedDB.databases();
  for (const database of databases) {
    if (!database.name) continue;

    try {
      const dbRequest = indexedDB.open(database.name);
      const db = await new Promise<IDBDatabase>((resolve, reject) => {
        dbRequest.onerror = () => reject(dbRequest.error);
        dbRequest.onsuccess = () => resolve(dbRequest.result);
      });

      const stores = Array.from(db.objectStoreNames);
      for (const storeName of stores) {
        try {
          const transaction = db.transaction(storeName, 'readonly');
          const store = transaction.objectStore(storeName);

          const storeData = await new Promise<string[]>((resolve, reject) => {
            const request = store.getAll();
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
          }).then((data) =>
            Promise.all(takeRight(data, TELEMETRY_TRUNCATE_SIZE).map((item) => superjsonParseAsync(item))),
          );

          set(data, `idb.${database.name}.${storeName}`, storeData);
        } catch {
          set(data, `idb.${database.name}.${storeName}`, 'cannot read data');
        }
      }

      db.close();
    } catch {
      set(data, `idb.${database.name}`, 'cannot open database');
    }
  }

  return JSON.stringify(data);
}

export default function useTelemetry() {
  const queryClient = useQueryClient();

  const mutation = useMutation<RespondedType, ResponseError, File>({
    mutationKey: ['/UploadLogData'],
    mutationFn: (file) =>
      fetcher(
        '/UploadLogData',
        { file },
        {
          requestContentType: 'form-data',
          validResponseCodes: [0],
          authPlacement: 'Data',
          timeout: 300_000,
          target: 'file-upload',
        },
      ),
    onError: undefined,
    onSuccess: () => {
      queryClient.setQueryData(systemSettingQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (!draft) return;

          draft.enableRecordUpload = false;
        }),
      );
      addTranslateToast({
        type: 'success',
        message: 'upload.success',
      });
    },
  });

  const sendTelemetry = useCallback(async () => {
    try {
      const data = await collectTelemetry();

      const blob = new Blob([data], { type: 'application/json;charset=utf-8' });
      const file = new File([blob], `U${getUserId()}-${Date.now()}.json`, { type: 'application/json;charset=utf-8' });

      return mutation.mutateAsync(file);
    } catch {
      addTranslateToast({
        type: 'error',
        message: 'upload.fail',
      });
    }
  }, [mutation.mutateAsync]);

  return {
    ...mutation,
    sendTelemetry,
  };
}

export function useAutoSendTelemetry() {
  const { data } = useQuery({ ...systemSettingQueryOptions, select: (data) => data.enableRecordUpload });
  const { sendTelemetry } = useTelemetry();

  useEffect(() => {
    if (data) {
      sendTelemetry().catch(console.warn);
    }
  }, [data, sendTelemetry]);
}
